/**
 * BlocksPreviewSlot
 *
 * Unified behaviour slot for both `insight_preview` and `report_preview`
 * experiences. Renders blocks with fullscreen expansion and optional
 * footer CTAs (scheduling, Slack integration, PDF download).
 *
 * **Block source (dual-mode):**
 * - Inline: `action.data.blocks` provided directly by the backend (preferred)
 * - Fetched: Falls back to `useInsightReports(action.data.insight_id)` when
 *   no inline blocks are present (backward compat for older report_preview cards)
 *
 * **CTAs:**
 * - Shown when `action.experience === 'insight_preview'`
 * - Hidden for `report_preview` experience
 *
 * Replaces the former `InsightPreviewSlot` and `ReportPreviewSlot` components.
 */

import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'motion/react';
import { Maximize2 } from 'lucide-react';
import { toast } from '@strivelabs/ui-core';
import { LoadingSkeleton } from '@strivelabs/ui-blocks';
import { log } from '@/lib/logger';
import type {
  BehaviourAgentExperienceAction,
  UIActionEvent,
  UIBlock,
  ContainerBlock,
  InsightCardBlock,
  ReportBlock,
} from '@/types/ui-blocks';
import { isFlatReportData, isMultiPageReportData } from '@/types/ui-blocks';
import { dispatchBlock, isContainer, renderContainerBlock } from '../block-renderer-utils';
import { InsightCardBlockRenderer } from '../insight-card-block-renderer';
import { useChatMetadata } from '@/contexts/ChatMetadataContext';
import { useUpdateBehaviourActionState } from '@/hooks/useUpdateBehaviourActionState';
import { ScheduleReportOverlay } from '@/components/schedule/ScheduleReportOverlay';
import { SendToSlackOverlay } from '@/components/slack/SendToSlackOverlay';
import { useGeneratePdfFromBlocks } from '@/hooks/useGeneratePdfFromBlocks';
import { useInsightReports } from '@/hooks/useInsights';
import { REPORT_TYPE } from '@/constants/reports';
import {
  FullscreenSlotOverlay,
  SlotContainer,
  downloadPdfFromUrl,
  isInsightCardBlock,
} from './shared';

// Slot action icons
import scheduleIcon from '@/assets/slot/schedule.svg';
import slackLogo from '@/assets/slot/slack-logo.png';
import downloadIcon from '@/assets/slot/download.svg';

// ---------------------------------------------------------------------------
// Block rendering
// ---------------------------------------------------------------------------

/**
 * Recursively render blocks within the slot.
 * Handles InsightCardBlock, ContainerBlock, and regular UIBlock.
 */
const renderSlotBlocks = (
  blocks: (UIBlock | ContainerBlock | InsightCardBlock)[],
  onUIAction: (event: UIActionEvent) => void,
  readOnly?: boolean
): React.ReactNode[] => {
  return blocks.map((block, index) => {
    if (isInsightCardBlock(block)) {
      return (
        <div key={`insight-card-${index}`}>
          <InsightCardBlockRenderer block={block} onUIAction={onUIAction} />
        </div>
      );
    }

    const uiBlock = block as UIBlock | ContainerBlock;

    if (isContainer(uiBlock)) {
      return renderContainerBlock(
        uiBlock as ContainerBlock,
        (innerBlocks) => renderSlotBlocks(innerBlocks, onUIAction, readOnly),
        { includeWrapper: true, index }
      );
    }

    return (
      <div key={`slot-block-${index}`}>
        {dispatchBlock({ block: uiBlock, onUIAction, readOnly })}
      </div>
    );
  });
};

// ---------------------------------------------------------------------------
// Fetch-path helpers (backward compat for old report_preview cards)
// ---------------------------------------------------------------------------

/**
 * Resolves the target page's blocks from a MULTI_PAGE report payload.
 */
function resolveMultiPageBlocks(
  pageGroups: Array<{ id: string; pages: Array<{ id?: string; blocks: ReportBlock[] }> }>,
  pageGroupId?: string,
  pageId?: string
): ReportBlock[] {
  const group =
    (pageGroupId ? pageGroups.find((g) => g.id === pageGroupId) : undefined) ?? pageGroups[0];

  if (!group) return [];

  const page =
    (pageId ? group.pages.find((p) => p.id === pageId) : undefined) ?? group.pages[0];

  return page?.blocks ?? [];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface BlocksPreviewSlotProps {
  action: BehaviourAgentExperienceAction;
  onUIAction: (event: UIActionEvent) => void;
  readOnly?: boolean;
  chatHistoryId?: string;
  behaviourActionState?: Record<string, unknown>;
}

export function BlocksPreviewSlot({
  action,
  onUIAction,
  readOnly,
  chatHistoryId,
  behaviourActionState,
}: BlocksPreviewSlotProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isScheduleDrawerOpen, setIsScheduleDrawerOpen] = useState(false);
  const [isSendToSlackDrawerOpen, setIsSendToSlackDrawerOpen] = useState(false);
  const [shouldAnimateTowardsIcon, setShouldAnimateTowardsIcon] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const [chatRoot, setChatRoot] = useState<HTMLElement | null>(null);

  // -- Block source resolution ------------------------------------------------

  // Inline blocks (insight_preview cards + new report_preview cards)
  const directBlocks = action.data?.blocks;
  const hasDirectBlocks = Array.isArray(directBlocks) && directBlocks.length > 0;

  // Fetch path (old report_preview cards with insight_id but no inline blocks)
  const insightId = action.data?.insight_id ?? '';
  const shouldFetch = !!insightId && !hasDirectBlocks;
  const { data: reports, isLoading, isError } = useInsightReports(insightId, {
    enabled: shouldFetch,
  });

  // Resolve blocks from fetched reports (only used when shouldFetch is true)
  const fetchedBlocks: ReportBlock[] = (() => {
    if (!reports || reports.length === 0) return [];

    const targetReportId = action.data?.insight_report_id;
    const targetPageGroupId = action.data?.page_group_id;
    const targetPageId = action.data?.page_id;

    // 1. Direct report lookup by insight_report_id
    const targetReport = targetReportId
      ? reports.find((r) => String(r.id) === String(targetReportId))
      : undefined;

    // 2. If no direct match, search MULTI_PAGE reports by page_group_id
    const report =
      targetReport ??
      (targetPageGroupId &&
        reports.find(
          (r) =>
            (r.type || REPORT_TYPE.FLAT) === REPORT_TYPE.MULTI_PAGE &&
            isMultiPageReportData(r.data) &&
            r.data.page_groups.some((g) => g.id === targetPageGroupId)
        )) ??
      // 3. Fallback to first report
      reports[0];

    const reportType = report.type || REPORT_TYPE.FLAT;

    if (reportType === REPORT_TYPE.FLAT) {
      if (isFlatReportData(report.data)) {
        return report.data.ui.blocks;
      }
      return [];
    }

    if (isMultiPageReportData(report.data)) {
      return resolveMultiPageBlocks(report.data.page_groups, targetPageGroupId, targetPageId);
    }
    return [];
  })();

  // Final blocks to render — inline takes precedence
  const resolvedBlocks = hasDirectBlocks
    ? (directBlocks as (UIBlock | ContainerBlock | InsightCardBlock)[])
    : (fetchedBlocks as (UIBlock | ContainerBlock | InsightCardBlock)[]);

  // -- CTA visibility ---------------------------------------------------------

  const showCTAs = action.experience === 'insight_preview';

  // -- CTA hooks & state (always called, only used when showCTAs is true) -----

  const { generatePdf, isGenerating: isGeneratingPdf } = useGeneratePdfFromBlocks();
  const { workflowInstanceId, workflowExecutionId } = useChatMetadata();

  const actionState =
    action.id && behaviourActionState
      ? (behaviourActionState[action.id] as Record<string, unknown> | undefined)
      : undefined;
  const initialScheduled = (actionState?.scheduled as boolean) ?? false;
  const initialSentToSlack = (actionState?.sent_to_slack as boolean) ?? false;
  const [localScheduledState, setLocalScheduledState] = useState(initialScheduled);
  const [localSentToSlackState, setLocalSentToSlackState] = useState(initialSentToSlack);

  const scheduleMutation = useUpdateBehaviourActionState({
    onSuccess: () => {
      setLocalScheduledState(true);
      toast({ title: 'Report scheduled successfully', variant: 'default' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to schedule report',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
    },
  });

  const sendToSlackMutation = useUpdateBehaviourActionState({
    onSuccess: () => {
      setLocalSentToSlackState(true);
      toast({ title: 'Sent to Slack successfully', variant: 'default' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to send to Slack',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
    },
  });

  // -- CTA handlers -----------------------------------------------------------

  const handleScheduleReport = async () => {
    if (!workflowInstanceId || !workflowExecutionId || !action.id) {
      toast({ title: 'Cannot schedule report', description: 'Missing required IDs', variant: 'destructive' });
      return;
    }
    if (!chatHistoryId) {
      toast({
        title: 'Cannot schedule report',
        description: 'Message ID not available yet. Please wait for the message to complete.',
        variant: 'destructive',
      });
      return;
    }
    setIsScheduleDrawerOpen(true);
  };

  const handleScheduleSuccess = () => {
    if (workflowInstanceId && workflowExecutionId && chatHistoryId && action.id) {
      setShouldAnimateTowardsIcon(true);
      scheduleMutation.mutate({
        workflowInstanceId,
        workflowExecutionId,
        chatHistoryId,
        behaviourActionStateId: action.id,
        state: { scheduled: true },
      });
      setTimeout(() => {
        setIsScheduleDrawerOpen(false);
      }, 100);
    } else {
      log('error', 'Missing required IDs to update behaviour action state after scheduling.');
      setIsScheduleDrawerOpen(false);
    }
  };

  const handleOverlayExitComplete = () => {
    if (shouldAnimateTowardsIcon) {
      setShouldAnimateTowardsIcon(false);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('report-scheduled'));
      }, 100);
    }
  };

  const handleSendToSlack = () => {
    if (!workflowExecutionId) {
      toast({ title: 'Cannot send to Slack', description: 'Missing workflow execution ID', variant: 'destructive' });
      return;
    }
    setIsSendToSlackDrawerOpen(true);
  };

  const handleSendToSlackSuccess = () => {
    if (workflowInstanceId && workflowExecutionId && chatHistoryId && action.id) {
      sendToSlackMutation.mutate({
        workflowInstanceId,
        workflowExecutionId,
        chatHistoryId,
        behaviourActionStateId: action.id,
        state: { sent_to_slack: true },
      });
      setIsSendToSlackDrawerOpen(false);
    } else {
      log('error', 'Missing required IDs to update behaviour action state after sending to Slack.');
      setIsSendToSlackDrawerOpen(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!resolvedBlocks || resolvedBlocks.length === 0) {
      toast({ title: 'Cannot download report', description: 'No report content available', variant: 'destructive' });
      return;
    }
    try {
      const reportTitle = action.data?.title || 'Report';
      const pdfResult = await generatePdf({ blocks: resolvedBlocks, title: reportTitle });
      await downloadPdfFromUrl(pdfResult.url, `${reportTitle.replace(/\s+/g, '_')}.pdf`);
      toast({ title: 'Report downloaded successfully', variant: 'default' });
    } catch (error) {
      toast({
        title: 'Failed to download report',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    }
  };

  const isScheduleDisabled = localScheduledState || scheduleMutation.isPending;

  // -- Effects ----------------------------------------------------------------

  useEffect(() => {
    if (slotRef.current) {
      const root = slotRef.current.closest<HTMLElement>('[data-chat-root]');
      if (root) setChatRoot(root);
    }
  }, []);

  // -- Render -----------------------------------------------------------------

  // Loading state (fetch path only)
  if (shouldFetch && isLoading) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <LoadingSkeleton />
        </div>
      </SlotContainer>
    );
  }

  // Error or empty state
  if (resolvedBlocks.length === 0) {
    // Fetch path: show error message for failed/empty fetches
    if (shouldFetch && (isError || !reports || reports.length === 0)) {
      return (
        <SlotContainer slotRef={slotRef}>
          <div className="p-10">
            <p className="text-sm text-muted-foreground">Unable to load report.</p>
          </div>
        </SlotContainer>
      );
    }
    // Inline path with no blocks: render nothing (matches old InsightPreviewSlot)
    return null;
  }

  return (
    <>
      <SlotContainer slotRef={slotRef}>
        {/* Expand button - positioned at top right */}
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="absolute top-2.5 right-2.5 z-10 size-6 flex items-center justify-center rounded-md bg-white/60 border border-black/10 shadow-sm hover:bg-white/80 transition-colors"
          aria-label="Expand to fullscreen"
        >
          <Maximize2 className="size-3.5 text-foreground" />
        </button>

        {/* Slot content */}
        <div className="p-10 space-y-7">
          {renderSlotBlocks(resolvedBlocks, onUIAction, readOnly)}
        </div>

        {/* Footer with CTAs — only for insight_preview */}
        {showCTAs && (
          <div className="relative border-t border-black/[0.04] h-12 flex items-center justify-between px-2">
            {/* Left side - Schedule report */}
            <button
              type="button"
              onClick={handleScheduleReport}
              disabled={isScheduleDisabled}
              className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-white border border-black/[0.08] text-sm text-foreground hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={localScheduledState ? 'Already scheduled' : 'Schedule report'}
              title={localScheduledState ? 'Already scheduled' : 'Schedule this report'}
            >
              <img src={scheduleIcon} alt="" className="size-[18px]" />
              <span className="font-normal tracking-[-0.28px]">Schedule report</span>
            </button>
            {/* Right side - Send to slack & Download report */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSendToSlack}
                className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-white border border-black/[0.08] text-sm text-foreground hover:bg-gray-50 transition-colors"
                aria-label={localSentToSlackState ? 'Re-send to Slack' : 'Send to Slack'}
              >
                <img src={slackLogo} alt="" className="size-[13px] object-contain" />
                <span className="font-normal tracking-[-0.28px]">
                  {localSentToSlackState ? 'Re-send to Slack' : 'Send to Slack'}
                </span>
              </button>

              <button
                type="button"
                onClick={handleDownloadReport}
                disabled={isGeneratingPdf}
                className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-white border border-black/[0.08] text-sm text-foreground hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Download report"
              >
                <img src={downloadIcon} alt="" className="size-3.5" />
                <span className="font-normal tracking-[-0.28px]">
                  {isGeneratingPdf ? 'Generating PDF...' : 'Download report'}
                </span>
              </button>
            </div>
          </div>
        )}
      </SlotContainer>

      {/* Fullscreen overlay — portalled into the chat container */}
      <AnimatePresence>
        {isExpanded && chatRoot && (
          <FullscreenSlotOverlay onClose={() => setIsExpanded(false)} portalTarget={chatRoot}>
            {renderSlotBlocks(resolvedBlocks, onUIAction, readOnly)}
          </FullscreenSlotOverlay>
        )}
      </AnimatePresence>

      {/* Schedule Report Fullscreen Overlay */}
      {showCTAs && (
        <AnimatePresence mode="wait" onExitComplete={handleOverlayExitComplete}>
          {isScheduleDrawerOpen && (
            <ScheduleReportOverlay
              key="schedule-overlay"
              isOpen={isScheduleDrawerOpen}
              workflowExecutionId={workflowExecutionId || ''}
              blocks={resolvedBlocks || []}
              title={action.data?.title}
              onClose={() => setIsScheduleDrawerOpen(false)}
              onSuccess={handleScheduleSuccess}
              animateTowardsIcon={shouldAnimateTowardsIcon}
            />
          )}
        </AnimatePresence>
      )}

      {/* Send to Slack Fullscreen Overlay */}
      {showCTAs && (
        <AnimatePresence mode="wait">
          {isSendToSlackDrawerOpen && (
            <SendToSlackOverlay
              key="slack-overlay"
              isOpen={isSendToSlackDrawerOpen}
              blocks={resolvedBlocks || []}
              title={action.data?.title}
              onClose={() => setIsSendToSlackDrawerOpen(false)}
              onSuccess={handleSendToSlackSuccess}
            />
          )}
        </AnimatePresence>
      )}
    </>
  );
}
